import { Drawer } from '@/components/modules';
import {
  useCreateInspectionMutation,
  useDeleteInspectionMutation,
  useInspectionsBySiteQuery,
  useUpdateInspectionMutation,
} from '@/remote/inspections';
import type { SiteData, SiteInspectionResponse } from '@/types/site';
import React, { Suspense, useState } from 'react';

interface InspectionDrawerProps {
  isOpen: boolean;
  selectedSite: SiteData | null;
  onClose: () => void;
}

interface InspectionContentProps {
  selectedSite: SiteData;
}

interface InspectionFormDrawerProps {
  isOpen: boolean;
  title: string;
  initialData?: {
    title: string;
    inspectionDate: string;
    contents: string;
  };
  onSubmit: (data: {
    title: string;
    inspectionDate: string;
    contents: string;
  }) => void;
  onClose: () => void;
  isLoading: boolean;
}

// 점검기록 폼 드로어 컴포넌트
function InspectionFormDrawer({
  isOpen,
  title,
  initialData,
  onSubmit,
  onClose,
  isLoading,
}: Readonly<InspectionFormDrawerProps>) {
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    onSubmit({
      title: formData.get('title') as string,
      inspectionDate: formData.get('inspectionDate') as string,
      contents: formData.get('contents') as string,
    });
  };

  const today = new Date().toISOString().split('T')[0];

  if (!isOpen) return null;

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title={title} width="w-[500px]">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label
            htmlFor="inspection-title"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            제목 *
          </label>
          <input
            id="inspection-title"
            type="text"
            name="title"
            defaultValue={initialData?.title || ''}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="점검 제목을 입력하세요"
            required
            disabled={isLoading}
          />
        </div>
        <div>
          <label
            htmlFor="inspection-date"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            점검 날짜 *
          </label>
          <input
            id="inspection-date"
            type="date"
            name="inspectionDate"
            defaultValue={initialData?.inspectionDate || today}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
            disabled={isLoading}
          />
        </div>
        <div>
          <label
            htmlFor="inspection-contents"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            점검 내용
          </label>
          <textarea
            id="inspection-contents"
            name="contents"
            rows={8}
            defaultValue={initialData?.contents || ''}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            placeholder="점검 내용을 상세히 입력하세요"
            disabled={isLoading}
          />
        </div>
        <div className="flex space-x-3 pt-4">
          <button
            type="submit"
            disabled={isLoading}
            className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-3 rounded-lg cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                처리 중...
              </>
            ) : (
              (() => {
                return initialData ? '수정 완료' : '기록 추가';
              })()
            )}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 py-3 rounded-lg cursor-pointer whitespace-nowrap transition-colors"
          >
            취소
          </button>
        </div>
      </form>
    </Drawer>
  );
}

// 점검 기록 내용을 처리하는 내부 컴포넌트
function InspectionContent({ selectedSite }: Readonly<InspectionContentProps>) {
  const [editingInspection, setEditingInspection] =
    useState<SiteInspectionResponse | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  // API 훅들
  const { data: inspections } = useInspectionsBySiteQuery(selectedSite.siteId);
  const createMutation = useCreateInspectionMutation();
  const updateMutation = useUpdateInspectionMutation();
  const deleteMutation = useDeleteInspectionMutation();

  const handleAddInspection = () => {
    setShowCreateForm(true);
  };

  const handleEditInspection = (record: SiteInspectionResponse) => {
    setEditingInspection(record);
  };

  const handleDeleteInspection = async (inspectionId: string) => {
    if (window.confirm('이 점검기록을 삭제하시겠습니까?')) {
      try {
        await deleteMutation.mutateAsync(inspectionId);
      } catch (error) {
        console.error('점검기록 삭제 실패:', error);
        alert('점검기록 삭제에 실패했습니다.');
      }
    }
  };

  const handleCreateSubmit = async (data: {
    title: string;
    inspectionDate: string;
    contents: string;
  }) => {
    try {
      await createMutation.mutateAsync({
        siteId: selectedSite.siteId,
        title: data.title,
        inspectionDate: data.inspectionDate,
        contents: data.contents,
      });
      setShowCreateForm(false);
    } catch (error) {
      console.error('점검기록 생성 실패:', error);
      alert('점검기록 생성에 실패했습니다.');
    }
  };

  const handleUpdateSubmit = async (data: {
    title: string;
    inspectionDate: string;
    contents: string;
  }) => {
    if (!editingInspection) return;

    try {
      await updateMutation.mutateAsync({
        id: editingInspection.id,
        data: {
          title: data.title,
          inspectionDate: data.inspectionDate,
          contents: data.contents,
        },
      });
      setEditingInspection(null);
    } catch (error) {
      console.error('점검기록 수정 실패:', error);
      alert('점검기록 수정에 실패했습니다.');
    }
  };

  const headerActions = (
    <button
      onClick={handleAddInspection}
      className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
      disabled={createMutation.isPending}
    >
      <div className="w-4 h-4 flex items-center justify-center">
        <i className="ri-add-line"></i>
      </div>
      <span>점검기록 추가</span>
    </button>
  );

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">점검기록 목록</h3>
          {headerActions}
        </div>
        <div className="divide-y divide-gray-200">
          {inspections.length === 0 ? (
            <div className="px-6 py-8 text-center">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 bg-gray-100 rounded-lg">
                <i className="ri-file-list-3-line text-gray-400 text-2xl"></i>
              </div>
              <p className="text-gray-500">등록된 점검기록이 없습니다.</p>
              <button
                onClick={handleAddInspection}
                className="mt-3 text-blue-600 hover:text-blue-800 cursor-pointer whitespace-nowrap"
              >
                첫 점검기록을 추가해보세요
              </button>
            </div>
          ) : (
            inspections.map((record: SiteInspectionResponse) => (
              <div
                key={record.id}
                className="px-6 py-4 hover:bg-gray-50 transition-colors group"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h4 className="text-lg font-medium text-gray-900">
                        {record.title}
                      </h4>
                      <span className="text-sm text-gray-500">
                        {record.inspectionDate}
                      </span>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {record.contents}
                    </p>
                  </div>
                  <div className="opacity-100 flex items-center space-x-2 lg:opacity-0 group-hover:opacity-100 transition-opacity ml-4">
                    <button
                      onClick={() => handleEditInspection(record)}
                      className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg cursor-pointer"
                      title="수정"
                      disabled={updateMutation.isPending}
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-edit-line"></i>
                      </div>
                    </button>
                    <button
                      onClick={() => handleDeleteInspection(record.id)}
                      className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg cursor-pointer"
                      title="삭제"
                      disabled={deleteMutation.isPending}
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-delete-bin-line"></i>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* 생성 폼 드로어 */}
      <InspectionFormDrawer
        isOpen={showCreateForm}
        title="점검기록 추가"
        onSubmit={handleCreateSubmit}
        onClose={() => setShowCreateForm(false)}
        isLoading={createMutation.isPending}
      />

      {/* 수정 폼 드로어 */}
      <InspectionFormDrawer
        isOpen={!!editingInspection}
        title="점검기록 수정"
        initialData={
          editingInspection
            ? {
                title: editingInspection.title,
                inspectionDate: editingInspection.inspectionDate,
                contents: editingInspection.contents,
              }
            : undefined
        }
        onSubmit={handleUpdateSubmit}
        onClose={() => setEditingInspection(null)}
        isLoading={updateMutation.isPending}
      />
    </>
  );
}

export default function InspectionDrawer({
  isOpen,
  selectedSite,
  onClose,
}: Readonly<InspectionDrawerProps>) {
  if (!isOpen || !selectedSite) return null;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={`${selectedSite.address || selectedSite.name} 점검기록`}
      subtitle={selectedSite.name}
      width="w-[67vw]"
      headerActions={
        // 닫기 버튼
        <div className="items-center space-x-2 hidden">
          <button
            onClick={onClose}
            className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          ></button>
        </div>
      }
    >
      <Suspense
        fallback={
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
              <p className="text-gray-500">점검기록을 불러오는 중...</p>
            </div>
          </div>
        }
      >
        <InspectionContent selectedSite={selectedSite} />
      </Suspense>
    </Drawer>
  );
}
